import {
  Component,
  ElementRef,
  inject,
  OnInit,
  viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import { AppMenuitem } from './app.menuitem';
import {
  AppMenuService,
  AuthService,
  OrgSelectionService,
  NonProdReleaseAppInfo,
} from '../../../../services';
import { MenuBarItem } from '../../../../types';
import { UnsubscribeOnDestroy } from '../../../../classes';
import { filter, takeUntil } from 'rxjs';

@Component({
  selector: '[app-menu]',
  standalone: true,
  imports: [CommonModule, AppMenuitem, RouterModule, TooltipModule],
  template: `
    <div class="d-flex">
      <div style="flex:1;">
        <ul class="layout-menu" #menuContainer>
          <ng-container *ngFor="let item of model; let i = index">
            @if (!item.separator) {
            <li
              app-menuitem
              [item]="item"
              [index]="i"
              [root]="true"
            ></li>
            }
            @if (item.separator) {
            <li class="menu-separator"></li>
            }
          </ng-container>
        </ul>
      </div>
      @if (isPurchaseOrders) {
      <div
        style="flex:1;"
        class="submenu-header globalsearch ui-inputgroup d-none d-lg-flex justify-content-end"
      >
        <input
          type="text"
          placeholder="Search PO# / Trade Partner"
          id="globalSearchInput"
          #globalSearchInput
          (keyup.enter)="search(globalSearchInput.value)"
          (input)="globalSearch = globalSearchInput.value"
        />
        <span
          class="globalsearch ui-inputgroup-addon mr-1 px-2"
          (click)="search(globalSearchInput.value)"
        >
          <i class="fas fa-search"></i>
        </span>
        @if (globalSearch) {
        <span
          class="globalsearch ui-inputgroup-addon mr-1 px-2"
          (click)="clearGlobalSearch()"
        >
          <i class="pi pi-times"></i>
        </span>
        }
      </div>
      }

      @if (showInfoIcon) {
      <span
        class="menu-info-icon"
        [pTooltip]="infoTooltip"
        tooltipPosition="left"
      >
        <i class="pi pi-info-circle"></i>
      </span>
      }
    </div>
  `,
})
export class AppMenu extends UnsubscribeOnDestroy implements OnInit {
  el = inject(ElementRef);

  readonly menuContainer = viewChild.required<ElementRef>('menuContainer');
  appInfo: NonProdReleaseAppInfo | null = null;
  infoTooltip: string = '';
  showInfoIcon: boolean = false;
  isPurchaseOrders: boolean = false;
  model: any[] = [];
  appMenuItems: MenuBarItem[];
  selectedMarketId: string;
  isMobile: boolean = false;
  isTablet: boolean = false;
  globalSearch: string = '';

  readonly globalSearchInput = viewChild<ElementRef>('globalSearchInput');

  constructor(
    private appMenuService: AppMenuService,
    private authService: AuthService,
    private orgSelectionService: OrgSelectionService,
    private router: Router
  ) {
    super();
  }

  ngOnInit() {
    this.appMenuService.menuItems$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((items) => {
        this.appMenuItems = items;
        let menu: MenuBarItem[] = [];

        [...items].forEach((menuitem) => {
          let menuItem = this.checkMenuItem(menuitem);
          if (menuitem) menu.push(menuItem);
        });

        this.model = menu.filter((item) => item);
      });

    this.orgSelectionService.selectedMarket
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        filter((m) => m !== null)
      )
      .subscribe((m) => {
        this.selectedMarketId = m.sourceKey;
      });

    this.appMenuService.showPOSearch$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((showPOSearch) => (this.isPurchaseOrders = showPOSearch));

    // This is info displayed for QA in our non-prod environments
    this.appMenuService
      .getAppInfo()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: (info) => {
          this.appInfo = info;
          this.updateInfoTooltip();
        },
        error: (err) => {
          this.appInfo = null;
          this.updateInfoTooltip();
        },
      });
  }

  private updateInfoTooltip() {
    if (!this.appInfo) {
      this.infoTooltip = '';
      this.showInfoIcon = false;
      return;
    }

    let path = window.location.pathname.toLowerCase();
    let comp: any;
    const infoObj = this.appInfo as any;
    if (path.includes('schedules')) {
      comp = infoObj.PCPScheduleUI;
    } else if (path.includes('recordables')) {
      comp = infoObj.PCPRecordables;
    } else if (path.includes('purchaseorders')) {
      comp = infoObj.PCPPurchaseOrders;
    } else {
      comp = infoObj.PCPWebHost;
    }

    if (comp) {
      this.infoTooltip = `Build: ${comp.BuildNameVersion}\n Branch: ${comp.SourceBranch}`;
      this.showInfoIcon = true;
    } else {
      this.infoTooltip = '';
      this.showInfoIcon = false;
    }
  }

  checkMenuItem(menuItem: MenuBarItem): MenuBarItem {
    if (menuItem.claims && !this.authService.hasClaims(menuItem.claims))
      return null;

    if (menuItem.items) {
      let subItems: MenuBarItem[] = [];

      menuItem.items.forEach((item) => {
        let subItem: MenuBarItem = this.checkMenuItem(item);
        if (subItem) subItems.push(subItem);
      });

      if (subItems.length > 0) menuItem.items = subItems;
      else return null;
    }

    return menuItem;
  }

  search(searchString: string): void {
    const encodedSearchString = encodeURIComponent(searchString);
    this.router.navigate([
      'view',
      'search',
      'purchaseorders',
      this.selectedMarketId,
      { searchstring: encodedSearchString },
    ]);
  }

  clearGlobalSearch() {
    this.globalSearch = '';
    const globalSearchInput = this.globalSearchInput();
    if (globalSearchInput) {
      globalSearchInput.nativeElement.value = '';
    }
  }
}
